import { ROLES } from '../contracts/roles';

/**
 * Central access rules of the frontend: the single place that says which roles
 * may see or use what. Route guards (accessGuard), menu additions, widgets and
 * isAdmin() all read from here via canAccess() in utils.ts.
 *
 * Naming convention:
 * - A rule that backs a Grow launcher / menu entry is named after the launcher
 *   ALIAS of the Grow launcher seed (sitos-x-ui/powershell/sitos-x-ui.psm1,
 *   Get-GrowMenu), e.g. STATISTICS_REDESIGN.
 * - Every other rule gets a descriptive UPPER_SNAKE name (ADMIN_AREA,
 *   WIDGET_LAST_COMPLETIONS, ...).
 *
 * Migration path for the seed: once the frontend no longer gets its menu from
 * Grow, move the seed entries over 1:1: `Alias` becomes the rule key and
 * `ShowForRoles '{sitos-x-api}X'` becomes `ROLES.X`.
 *
 * UI only: these rules hide pages and widgets, they do not secure any data.
 * The API enforces access to the data.
 */
export const ACCESS_RULES = {
  /** Administrative area; basis of isAdmin(). */
  ADMIN_AREA: [ROLES.ADMIN, ROLES.EDUCATION_MANAGER, ROLES.UNIT_MANAGER],

  /**
   * Reporting/statistics pages. Narrower than ADMIN_AREA: UNIT_MANAGER is
   * excluded. Same as ShowForRoles of the seed launcher and the statistics
   * routes of sitos-x-ui.
   */
  STATISTICS_REDESIGN: [ROLES.ADMIN, ROLES.EDUCATION_MANAGER],

  /**
   * Dashboard widget "last completions". Deliberately narrower than ADMIN_AREA:
   * the backend returns completions only to these two roles (ADMIN: whole
   * tenant, EDUCATION_MANAGER: their groups); a UNIT_MANAGER would only ever
   * see the empty state.
   */
  WIDGET_LAST_COMPLETIONS: [ROLES.ADMIN, ROLES.EDUCATION_MANAGER],

  /** Dashboard widget "learning activity": only meant for administrative roles. */
  WIDGET_LEARNING_ACTIVITY: [ROLES.ADMIN, ROLES.EDUCATION_MANAGER, ROLES.UNIT_MANAGER],
} as const satisfies Record<string, readonly ROLES[]>;

export type AccessRule = keyof typeof ACCESS_RULES;
